/**
 * Shared Dialog primitive — wraps @radix-ui/react-dialog with YADA theme tokens.
 * - Radix handles focus trap, ESC, scroll lock, click-outside.
 * - framer-motion handles enter/exit (opacity + scale, reduced-motion aware).
 * - Tailwind: preflight already applies; do NOT add `box-sizing` overrides.
 */
import type { ReactNode } from 'react'
import * as RadixDialog from '@radix-ui/react-dialog'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'

export const Dialog = RadixDialog.Root
export const DialogTrigger = RadixDialog.Trigger
export const DialogPortal = RadixDialog.Portal
export const DialogClose = RadixDialog.Close

interface ContentProps {
  children: ReactNode
  className?: string
  /** Width class — uses YADA tokens. Default `max-w-lg`. */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
}

const SIZE_CLASS: Record<NonNullable<ContentProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  // `full` is for document-shaped content (previews, code, wide tables) where
  // `max-w-4xl` still letterboxes the payload. It hands the inner padding to
  // the caller's own chrome, so the scrollable region can sit flush to the
  // panel edges; a fixed height keeps the title and actions on screen.
  full: 'max-w-6xl h-[min(92dvh,60rem)] flex flex-col overflow-hidden p-0',
}

export function DialogOverlay({ className = '' }: { className?: string }) {
  const reduced = useReducedMotion()
  return (
    <RadixDialog.Overlay asChild forceMount>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: reduced ? 0 : 0.15, ease: 'easeOut' }}
        className={`fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm ${className}`}
      />
    </RadixDialog.Overlay>
  )
}

export function DialogContent({ children, className = '', size = 'md' }: ContentProps) {
  const reduced = useReducedMotion()
  return (
    <RadixDialog.Content asChild forceMount>
      <motion.div
        initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.96 }}
        transition={{ duration: reduced ? 0 : 0.18, ease: [0.4, 0, 0.2, 1] }}
        className={`fixed z-50 bg-surface border border-surface-3 shadow-yada-lg
                    p-6 focus:outline-none
                    inset-0 sm:inset-auto
                    sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2
                    w-full sm:w-[calc(100vw-2rem)] ${SIZE_CLASS[size]}
                    sm:rounded-yada-lg rounded-none
                    max-h-screen sm:max-h-[90vh] overflow-y-auto
                    ${className}`}
      >
        {children}
      </motion.div>
    </RadixDialog.Content>
  )
}

export function DialogHeader({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`mb-4 space-y-1 ${className}`}>{children}</div>
}

export const DialogTitle = RadixDialog.Title
export const DialogDescription = RadixDialog.Description

export function DialogFooter({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`mt-6 flex justify-end gap-2 ${className}`}>{children}</div>
}

/**
 * Wrap DialogPortal + AnimatePresence so opening/closing animates both
 * overlay and content. Radix handles the conditional rendering of the
 * content; AnimatePresence lets us run the exit transition.
 */
export function DialogRoot({
  open,
  onOpenChange,
  children,
  size = 'md',
  className = '',
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  children: ReactNode
  size?: ContentProps['size']
  className?: string
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPortal forceMount>
            <DialogOverlay />
            <DialogContent size={size} className={className}>
              {children}
            </DialogContent>
          </DialogPortal>
        )}
      </AnimatePresence>
    </Dialog>
  )
}